// Converts a singular word to a plural word.
export const pluralize = (word: string): string => {
  if (word.length === 0) return word;

  // Handle words ending in "y" after a consonant -> "ies"
  // (e.g., "Category" -> "Categories", but "Day" -> "Days")
  if (word.endsWith('y') && word.length > 1) {
    const beforeY = word.slice(0, -1);
    const lastChar = beforeY[beforeY.length - 1];
    // If the character before 'y' is a consonant (not a, e, i, o, u)
    if (lastChar && !['a', 'e', 'i', 'o', 'u'].includes(lastChar.toLowerCase())) {
      return `${beforeY}ies`;
    }
  }

  // Handle words ending in "f" -> "ves" (e.g., "Leaf" -> "Leaves")
  if (word.endsWith('f') && word.length > 1) {
    return `${word.slice(0, -1)}ves`;
  }

  // Handle words ending in "fe" -> "ves" (e.g., "Knife" -> "Knives")
  if (word.endsWith('fe') && word.length > 2) {
    return `${word.slice(0, -2)}ves`;
  }

  // Handle words ending in s, sh, ch, x, z -> add "es" (e.g., "Compressor" -> "Compressors")
  const lastChar = word[word.length - 1];
  const lastTwoChars = word.slice(-2);
  if (
    lastChar === 's' ||
    lastChar === 'x' ||
    lastChar === 'z' ||
    lastTwoChars === 'sh' ||
    lastTwoChars === 'ch'
  ) {
    return `${word}es`;
  }

  // Default: add "s" (e.g., "Vessel" -> "Vessels")
  return `${word}s`;
};

// Words where the plural "ves" comes from a singular ending in "f" or "fe".
// All other "ves"-ending words (e.g. "Valves") are treated as regular "s" plurals.
const F_VES_WORDS = new Set([
  'calves',
  'dwarves',
  'elves',
  'halves',
  'hooves',
  'knives',
  'leaves',
  'lives',
  'loaves',
  'scarves',
  'selves',
  'shelves',
  'wives',
  'wolves',
]);

// Words where the plural "ves" comes from a singular ending in "fe" specifically.
const FE_VES_WORDS = new Set(['knives', 'lives', 'wives', 'scarves']);

// Converts a plural word to a singular word.
export const singularize = (word: string): string => {
  let singularWord = word;

  // Handle common pluralization rules in reverse
  if (word.endsWith('ies') && word.length > 3) {
    // "ies" -> "y" (e.g., "Compressors" doesn't match, but "Categories" -> "Category")
    singularWord = `${word.slice(0, -3)}y`;
  } else if (word.endsWith('ves') && word.length > 3) {
    // "ves" -> "f" or "fe" only for known f/fe-stem words (e.g. "Leaves" -> "Leaf").
    // Words like "Valves" are regular "s" plurals and just need the "s" removed.
    const lower = word.toLowerCase();
    if (F_VES_WORDS.has(lower)) {
      singularWord = FE_VES_WORDS.has(lower) ? `${word.slice(0, -3)}fe` : `${word.slice(0, -3)}f`;
    } else {
      singularWord = word.slice(0, -1);
    }
  } else if (word.endsWith('es') && word.length > 2) {
    // "es" -> remove "es" (e.g., "Compressors" -> "Compressor")
    // But check if it's a valid singularization
    const withoutEs = word.slice(0, -2);
    // If the word ends with s, sh, ch, x, z, it was pluralized with "es"
    if (
      withoutEs.endsWith('s') ||
      withoutEs.endsWith('sh') ||
      withoutEs.endsWith('ch') ||
      withoutEs.endsWith('x') ||
      withoutEs.endsWith('z')
    ) {
      singularWord = withoutEs;
    } else {
      // Otherwise, just remove "s"
      singularWord = word.slice(0, -1);
    }
  } else if (word.endsWith('s') && word.length > 1) {
    // Simple "s" plural (e.g., "Vessels" -> "Vessel")
    singularWord = word.slice(0, -1);
  }

  return singularWord;
};

export const toTitleCaseWords = (value: string): string =>
  value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Device kind with dashes and underscores turned into spaces (e.g. `hot_gas-valve` → `hot gas valve`). */
const normalizeKind = (kind: string): string =>
  kind.replaceAll('-', ' ').replaceAll('_', ' ').trim();

/** Singular display label of a device kind (e.g. `screw-compressor` → `Screw Compressor`). */
export const toLabelFromKind = (kind: string): string => toTitleCaseWords(normalizeKind(kind));

export const toPluralLabelFromKind = (kind: string): string => {
  const normalized = normalizeKind(kind);
  if (!normalized) {
    return '';
  }

  const words = normalized.split(' ').filter(Boolean);
  const lastWord = words.at(-1);
  if (!lastWord) {
    return toTitleCaseWords(normalized);
  }

  words[words.length - 1] = pluralize(lastWord);
  return toTitleCaseWords(words.join(' '));
};
